<template>
    <div class="table">
        <NoData v-if="!customers.length" :type="'potential-policies-table'" />
        <template v-else>
            <div class="table__header box box--default">
                <div class="table__label table__label--small">
                </div>
                <div class="table__label table__label--small">
                    {{ $t(`customers.${"tableHeader"}.${"category"}`) }}
                </div>
                <div class="table__label table__label--large">
                    {{ $t(`customers.${"tableHeader"}.${"name"}`) }}
                </div>
                <div class="table__label table__label--medium">
                    {{ $t(`customers.${"tableHeader"}.${"contact"}`) }}
                </div>
                <div class="table__label table__label--small">
                    {{ $t(`customers.${"tableHeader"}.${"zipCode"}`) }}
                </div>
                <div class="table__label table__label--medium">
                    {{ $t(`customers.${"tableHeader"}.${"city"}`) }}
                </div>
                <div class="table__label table__label--medium">
                    {{ $t(`customers.${"tableHeader"}.${"deadline"}`) }}
                </div>
                <div class="table__label table__label--large">
                    {{ $t(`customers.${"tableHeader"}.${"text"}`) }}
                </div>
                <div class="table__label table__label--small">
                    {{ $t(`customers.${"tableHeader"}.${"file"}`) }}
                </div>
                <div class="table__label table__label--small">
                    {{ $t(`customers.${"tableHeader"}.${"contract"}`) }}
                </div>
                <div class="table__label table__label--small"></div>
                <div class="table__label table__label--small"></div>
            </div>

            <div class="table__body">
                <div v-for="policy in customers" :key="policy.id" class="data-record">
                    <div v-if="!isMobileView" @click="routeToCustomer(policy.customer)"
                        class="customer row row--hover box box--default"
                        :class="[policy.priority && policy.priority.type ? `policy--type-${policy.priority.type}` : '']">
                        <div class="row__data row__data--small policy-icon">
                            <div :class="['icon', getPolicyIconClass(policy)]"></div>
                        </div>
                        <div class="row__data row__data--name row__data--small category">
                            {{ policy.category ? policy.category.code : '----' }}
                        </div>
                        <div class="row__data row__data--name row__data--large">
                            <span v-if="policy.customer">
                                {{ policy.customer.first_name }} {{ policy.customer.last_name }}
                            </span>
                            <span v-else> ----- </span>
                        </div>
                        <div @click.stop class="row__data row__data--contact row__data--medium">
                            <span v-if="policy.customer">
                                <a class="row__phone icon__phone"
                                    :href="getPhone(policy.customer) ? `tel:${getPhone(policy.customer)}` : 'javascript:void(0)'"
                                    :class="{ 'icon__phone--active': getPhone(policy.customer) }"></a>

                                <a class="row__mail icon__mail"
                                    :href="getEmail(policy.customer) ? `mailto:${getEmail(policy.customer)}` : 'javascript:void(0)'"
                                    :class="{ 'icon__mail--active': getEmail(policy.customer) }"></a>
                            </span>

                            <span v-else>
                                <a class="row__phone icon__phone"></a>
                                <a class="row__mail icon__mail"></a>
                            </span>
                        </div>

                        <div class="row__data row__data--zip row__data--small">
                            <template v-if="policy.customer && getFirstAddress(policy.customer)">
                                {{ getFirstAddress(policy.customer).zip || '----' }}
                            </template>
                            <template v-else>----</template>
                        </div>
                        <div class="row__data row__data--city row__data--medium">
                            <template v-if="policy.customer && getFirstAddress(policy.customer)">
                                {{ getFirstAddress(policy.customer).city || '----' }}
                            </template>
                            <template v-else>----</template>
                        </div>
                        <div class="row__data row__data--date row__data--medium">
                            {{ formatSimpleDate(policy.date) }}
                        </div>
                        <div class="row__data row__data--date row__data--large">
                            <EditableComment :comment="policy.text" :readonly="true" />
                        </div>

                        <div class="row__data row__data--small row__data--pdf icon__pdf"
                            :class="{ 'icon__pdf--active': policy.files && policy.files.length }"
                            @click.stop="openPolicyFilesModal(policy)">
                        </div>

                        <div v-if="canForPolicy('policy_create', policy)"
                            class="row__data row__data--small row__data--contract icon__contract"
                            @click.stop="handleConvertPotentialToPolicy(policy)">
                        </div>

                        <div v-if="canForPolicy('customer_edit', policy)"
                            class="row__data row__data--small row__data--edit icon__edit"
                            @click.stop="handleOpenPotentialPolicyModal(policy)">
                        </div>

                        <div v-else style="cursor:no-drop;"
                            class="row__data row__data--small row__data--edit icon__edit">
                        </div>

                        <div v-if="canForPolicy('customer_delete', policy)"
                            class="row__data row__data--small row__data--delete icon__delete tool-icon"
                            :class="{ 'icon__delete--active': activeDeleteId === policy.id }" tabindex="-1"
                            @focusout="activeDeleteId = null" @click.stop="activeDeleteId = policy.id">

                            <transition name="fade">
                                <div class="delete" v-if="activeDeleteId === policy.id">
                                    <div class="delete__text">
                                        {{ $t('deleteModal.question') }} {{ $t('deleteModal.potentialPolicy') }}?
                                    </div>
                                    <div class="delete__buttons">
                                        <div class="button button--confirm" @click.stop="confirmDelete(policy.id)">
                                        </div>
                                        <div class="button button--cancel" @click.stop="activeDeleteId = null">
                                        </div>
                                    </div>
                                </div>
                            </transition>
                        </div>

                        <div v-else style="cursor:no-drop;"
                            class="row__data row__data--small row__data--delete icon__delete">
                        </div>
                    </div>

                    <article v-if="isMobileView" class="data-card box box--default">
                        <header class="data-card__header">
                            <button type="button" class="data-card__identity" @click="routeToCustomer(policy.customer)">
                                <span class="data-card__avatar">
                                    <div
                                        :class="['icon', getPolicyIconClass(policy), policy.priority && policy.priority.type ? `policy--type-${policy.priority.type}` : '']">
                                    </div>
                                </span>

                                <span class="data-card__name" v-if="policy.category">{{ policy.category.code }}</span>
                            </button>


                            <div class="data-card__action row__data--action" role="button" tabindex="0"
                                :aria-label="`${$t('buttons.editBtn')} / ${$t('buttons.deleteBtn')}`"
                                @click.self="toggleCardActionsDropdown(policy.id)"
                                @keydown.enter.self="toggleCardActionsDropdown(policy.id)"
                                @keydown.space.prevent.self="toggleCardActionsDropdown(policy.id)">
                                <div v-if="showCardActionsDropdown === policy.id" class="row__dropdown">
                                    <div class="row__dropdown-item row__dropdown-item--pdf icon__pdf"
                                        @click.stop="openPolicyFilesModal(policy)">
                                        {{ $t('customers.tableHeader.file') }}
                                    </div>
                                    <div v-if="canForPolicy('policy_create', policy)"
                                        class="row__dropdown-item row__dropdown-item--contract icon__contract"
                                        @click.stop="handleConvertPotentialToPolicy(policy)">
                                        {{ $t('customers.tableHeader.contract') }}
                                    </div>
                                    <div v-if="canForPolicy('customer_edit', policy)"
                                        class="row__dropdown-item row__dropdown-item--edit icon__edit"
                                        @click.stop="handleOpenPotentialPolicyModal(policy)">
                                        {{ $t('buttons.editBtn') }}
                                    </div>
                                    <div v-if="canForPolicy('customer_delete', policy)"
                                        class="row__dropdown-item row__dropdown-item--delete icon__delete"
                                        @click.stop="handleDeleteAction(policy.id)">
                                        {{ $t('buttons.deleteBtn') }}
                                    </div>
                                </div>
                            </div>
                        </header>

                        <div v-if="getPhone(policy.customer) || getEmail(policy.customer)" class="data-card__contacts">
                            <a v-if="getPhone(policy.customer)" class="data-card__contact"
                                :href="getPhone(policy.customer) ? `tel:${getPhone(policy.customer)}` : 'javascript:void(0)'">
                                <span class="icon__phone--active"></span>
                                <span>{{ getPhone(policy.customer) }}</span>
                            </a>
                            <a v-if="getEmail(policy.customer)" class="data-card__contact"
                                :href="getEmail(policy.customer) ? `mailto:${getEmail(policy.customer)}` : 'javascript:void(0)'">
                                <span class="icon__mail--active"></span>
                                <span>{{ getEmail(policy.customer) }}</span>
                            </a>
                        </div>

                        <dl class="data-card__details">
                            <div v-if="policy.customer && (policy.customer.first_name || policy.customer.last_name)"
                                class="data-card__detail">
                                <dt>{{ $t(`customers.${"tableHeader"}.${"name"}`) }}</dt>
                                <dd>{{ policy.customer.first_name }} {{ policy.customer.last_name }}</dd>
                            </div>
                            <div v-if="policy.date" class="data-card__detail  ">
                                <dt>{{ $t(`customers.${"tableHeader"}.${"deadline"}`) }}</dt>
                                <dd>{{ formatSimpleDate(policy.date) }}</dd>
                            </div>

                            <div v-if="policy.customer && getFirstAddress(policy.customer)" class="data-card__detail">
                                <dt>{{ $t(`customers.${"tableHeader"}.${"city"}`) }}</dt>
                                <dd>{{ getFirstAddress(policy.customer).city }}</dd>
                            </div>

                            <div v-if="policy.customer && getFirstAddress(policy.customer)" class="data-card__detail">
                                <dt>{{ $t(`customers.${"tableHeader"}.${"zipCode"}`) }}</dt>
                                <dd>{{ getFirstAddress(policy.customer).zip }}</dd>
                            </div>

                            <div v-if="policy.text" class="data-card__detail data-card__detail-full">
                                <dt>{{ $t(`customers.${"tableHeader"}.${"text"}`) }}</dt>
                                <dd>{{ policy.text }}</dd>
                            </div>
                        </dl>

                        <div v-if="activeDeleteId === policy.id" class="delete delete--mobile">
                            <div class="delete__text">
                                {{ $t('deleteModal.question') }} {{ $t('deleteModal.potentialPolicy') }}?
                            </div>
                            <div class="delete__buttons">
                                <div class="button button--confirm" @click.stop="confirmDelete(policy.id)"></div>
                                <div class="button button--cancel" @click.stop="activeDeleteId = null"></div>
                            </div>
                        </div>
                    </article>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import NoData from '@/partials/NoData';
import EditableComment from '@/partials/EditableComment';
import {
    openPotentialPolicyModal,
    convertPotentialToPolicy,
} from '@/helpers/potentialPolicyModalHelpers';

// Props
const { customers } = defineProps({
    customers: {
        required: true,
        type: Array,
    },
});

// Emits
const emit = defineEmits(['refresh']);

// Composables
const store = useStore();
const router = useRouter();

// Reactive state
const activeDeleteId = ref(null);
const showCardActionsDropdown = ref(null);

const isMobileView = ref(false);

const updateMobileView = () => {
    isMobileView.value = window.innerWidth <= 900;
};

onMounted(() => {
    updateMobileView();
    window.addEventListener("resize", updateMobileView);
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", updateMobileView);
});


// Computed properties
const policyFilesModalIsPotential = computed(() => store.getters.policyFilesModalIsPotential);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);

// Watchers
watch(
    () => policyFilesModalIsPotential.value,
    (newVal) => {
        // When modal closes (newVal becomes false), refresh the table
        if (!newVal) {
            emit('refresh');
        }
    }
);

import { getCurrentInstance } from 'vue';
const { proxy } = getCurrentInstance();
function policyTeamId(policy) {
    return policy?.team_id
        ?? policy?.team?.id
        ?? policy?.customer?.team_id
        ?? policy?.customer?.team?.id
        ?? null;
}

function canForPolicy(permission, policy) {
    if (isSuperAdmin.value) return true;
    return store.getters.hasTeamPermission(permission, policyTeamId(policy));
}

// Methods
function routeToCustomer(customer) {
    const customerTeamId = customer.team_id ?? customer.team?.id ?? null;

    if (!isSuperAdmin.value && !store.getters.hasTeamPermission('customer_show', customerTeamId)) {
        return;
    }

    router.push({
        name: 'AccountDetails',
        params: {
            entity: 'potential-customer',
            id: customer.id,
        },
        query: {
            tab: 5,
            team_id: customer.team_id ?? customer.team?.id ?? undefined,
        },
    });
}

function toggleCardActionsDropdown(policyId) {
    showCardActionsDropdown.value = showCardActionsDropdown.value === policyId ? null : policyId;
}

function handleDeleteAction(policyId) {
    activeDeleteId.value = policyId;
    showCardActionsDropdown.value = null;
}

async function handleConvertPotentialToPolicy(policy) {
    if (!canForPolicy('policy_create', policy)) {
        return;
    }

    try {
        await convertPotentialToPolicy(proxy, policy);
    } catch (err) {
        console.error('convertPotentialToPolicy error:', err);
        store.commit('setPolicyIsConverting', false);
        store.commit('setConvertingPolicyData', {});
        store.commit('setShowAddPolicyModal', false);
    }
}

function getPolicyIconClass(policy) {
    const categoryId = policy.category?.id;
    return `icon__policy--${categoryId || 'krankenkasse'}`;
}

function formatSimpleDate(dateString) {
    if (!dateString) return '-----';

    const d = new Date(dateString);

    if (isNaN(d.getTime())) return dateString;

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    return `${day}.${month}.${year}`;
}

function getPhone(customer) {
    const phones = customer?.customer_phones;
    if (Array.isArray(phones) && phones.length > 0) {
        return phones[0].phone || phones[0].number || null;
    }
    return null;
}

function getEmail(customer) {
    const emails = customer?.customer_emails;
    if (Array.isArray(emails) && emails.length > 0) {
        return emails[0].email || null;
    }
    return null;
}

function getFirstAddress(customer) {
    const addresses = customer?.customer_addresses;
    return Array.isArray(addresses) && addresses.length > 0 ? addresses[0] : null;
}

async function confirmDelete(policyId) {
    const policy = customers.find(p => p.id === policyId);
    if (policy && !canForPolicy('customer_delete', policy)) {
        activeDeleteId.value = null;
        return;
    }

    activeDeleteId.value = null;
    try {
        await store.dispatch('deletePotentialPolicy', policyId);
        emit('refresh');
    } catch (err) {
        console.error(err);
    }
}

async function handleOpenPotentialPolicyModal(policy) {
    if (!canForPolicy('customer_edit', policy)) {
        return;
    }

    await openPotentialPolicyModal(proxy, policy);
}

function openPolicyFilesModal(policy) {
    if (!isSuperAdmin.value && !canForPolicy('policy_access', policy)) return;

    store.commit('setPolicyFileId', policy.id);
    store.commit('setPolicyFiles', policy.files);
    store.commit('setPolicyFilesModalIsPotential', true);
    store.commit('setCustomerFromDashboard', policy.customer.id);
    store.commit('setFilesFromDashboard', true);
    store.commit('setShowPolicyFilesModal', true);
}
</script>

<style scoped lang="scss">
.row__data--delete {
    position: relative;

    .delete {
        position: absolute;
        z-index: 3;
        background: var(--bg-1);
        right: 30%;
        top: 50%;
        transform: translateY(-50%);
        width: 330px;
        display: flex;
        align-items: center;
        border-radius: var(--radius-1);
        border: 1px solid var(--border-2);
        padding: 10px 20px;
        box-shadow: 0px 3px 6px var(--shadow-7);

        &__text {
            line-height: 20px;
            color: var(--text-9);
        }

        &__buttons {
            display: flex;
        }

        .button {
            width: 24px;
            height: 24px;
            cursor: pointer;
            transition: all 0.3s ease;

            &--confirm {
                background: url("../assets/icons/default/confirm_icon.svg");
                margin: 0 15px;

                &:hover {
                    background: url("../assets/icons/default/confirm_hover_icon.svg");
                }
            }

            &--cancel {
                background: url("../assets/icons/default/close_icon.svg");

                &:hover {
                    background: url("../assets/icons/default/close_hover_icon.svg");
                }
            }
        }
    }
}

.category {
    justify-content: center !important;
}

.data-card__avatar {
    .icon {
        padding: 20px;
        border-radius: 50px;
    }

    .policy--type-A {
        border: 1px solid #b20202;
    }

    .policy--type-B {
        border: 1px solid #ff5f00;
    }

    .policy--type-C {
        border: 1px solid #22db8e;
    }

    .policy--type-D {
        border: 1px solid #ffa400;
    }
}

.table__body .customer {
    position: relative;

    &:before {
        content: "";
        position: absolute;
        z-index: 3;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        height: 100%;
        width: 15px;
        border-top-left-radius: var(--radius-1);
        border-bottom-left-radius: var(--radius-1);
    }

    &.policy--type-A {
        &:before {
            background: #b20202;
        }

        .row--hover:hover {
            background: #b202021a;
        }
    }

    &.policy--type-B {
        &:before {
            background: #ff5f00;
        }

        .row--hover:hover {
            background: #ff5f001a;
        }
    }

    &.policy--type-C {
        &:before {
            background: #22db8e;
        }

        .row--hover:hover {
            background: #22db8e1a;
        }
    }

    &.policy--type-D {
        &:before {
            background: #ffa400;
        }

        .row--hover:hover {
            background: #ffa4001a;
        }
    }
}
</style>
